import React, { useCallback, useImperativeHandle } from "react"
import i18nData from "@rogue_4/locales/zh-CN.json"
import anime from "animejs"

import { i18n } from "@/utils/i18n"
import ANIMATION from "../../animation"
import Button from "../../components/Button"
import ModalTitle from "../../components/Modal/common/modalTitle"
import { useDivRef } from "../../hooks/useDivRef"
import { useGameRestart } from "../../store"
import { IAnimateRef } from "../../type"
import { SFX } from "../../utils/sound"

import styles from "./index.module.less"

const EndGameScene = React.forwardRef<IAnimateRef, { onExit: () => void }>(
    ({ onExit }, ref) => {
        const leftDoorRef = useDivRef()
        const rightDoorRef = useDivRef()
        const middleDecoRef = useDivRef()
        const handDecoRef = useDivRef()
        const maskBottomRef = useDivRef()
        const titleRef = useDivRef()
        const buttonsRef = useDivRef()
        useImperativeHandle(
            ref,
            () => ({
                fadeIn: async () => {
                    const inst = anime.timeline()
                    inst.add(ANIMATION.in(maskBottomRef), 400)
                    inst.add(ANIMATION.in(leftDoorRef, { right: "15rem" }), 400)
                    inst.add(ANIMATION.in(rightDoorRef, { left: "15rem" }), 400)
                    inst.add(
                        ANIMATION.in(
                            [handDecoRef, middleDecoRef],
                            {},
                            { duration: 500, scale: [0.7, 1] }
                        ),
                        400
                    )
                    inst.add(
                        ANIMATION.in(
                            [titleRef, buttonsRef],
                            { bottom: "2rem" },
                            { duration: 300 }
                        ),
                        600
                    )
                    return inst.finished
                },
                fadeOut: async () => {
                    const inst = anime.timeline()
                    inst.add(
                        ANIMATION.out(
                            [
                                maskBottomRef,
                                leftDoorRef,
                                rightDoorRef,
                                handDecoRef,
                                middleDecoRef,
                                titleRef,
                                buttonsRef,
                            ],
                            {},
                            { duration: 500 }
                        ),
                        0
                    )
                    return inst.finished
                },
            }),
            [
                buttonsRef,
                handDecoRef,
                leftDoorRef,
                maskBottomRef,
                middleDecoRef,
                rightDoorRef,
                titleRef,
            ]
        )
        const restart = useGameRestart()
        const handleRestart = useCallback(() => {
            SFX.play("commonClick")
            restart()
        }, [restart])
        const handleBack = useCallback(() => {
            SFX.play("commonClick")
            onExit()
        }, [onExit])
        return (
            <div className={styles.endGameScene}>
                <div className={styles.doors}>
                    <div className={styles.leftDoor} ref={leftDoorRef}></div>
                    <div className={styles.rightDoor} ref={rightDoorRef}></div>
                </div>
                <div className={styles.middleDeco} ref={middleDecoRef}></div>
                <div className={styles.handDeco} ref={handDecoRef}></div>
                <div className={styles.maskBottom} ref={maskBottomRef}></div>
                <ModalTitle noDeco className={styles.title} ref={titleRef}>
                    {i18n(i18nData, "rogue_4.game.endGame.notice")}
                </ModalTitle>
                <div className={styles.buttonGroup} ref={buttonsRef}>
                    <Button active={false} onClick={handleRestart} size="small">
                        {i18n(i18nData, "rogue_4.game.endGame.button.restart")}
                    </Button>
                    <Button active={false} onClick={handleBack} size="small">
                        {i18n(i18nData, "rogue_4.game.endGame.button.end")}
                    </Button>
                </div>
            </div>
        )
    }
)
EndGameScene.displayName = "EndGameScene"

export default EndGameScene
